import React, { useRef } from 'react';
import c from 'classnames';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import PopoverContainer from '../_partials/PopoverContainer';
import { PositionableProps } from '../types';
import s from './PopoverWrapper.scss';

interface Props extends PositionableProps {
  role?: string;
  'aria-label'?: string;
  className?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  arrowClassName?: string;
  children: React.ReactNode;
  width?: number;
  forceTheme?: ThemeOptions;
  onClickOutside?: (event: MouseEvent) => void;
  updatePositioning?: () => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

/**
 * A wrapper around PopoverContainer that matches our new DS specs.
 */
const PopoverWrapper: React.FC<Props> = ({
  effectivePlacement,
  effectiveSecondaryPlacement,
  arrowRef,
  rootRef,
  arrowStyle,
  children,
  className,
  wrapperClassName,
  contentClassName,
  arrowClassName,
  contentStyle,
  contentAttributes,
  arrowAttributes,
  role,
  'aria-label': ariaLabel,
  width,
  forceTheme,
  onClickOutside,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  updatePositioning,
}) => {
  const contentRef = useRef<HTMLElement | null>(null);

  return (
    <ThemeOverride theme={forceTheme || 'match'} className={wrapperClassName}>
      <PopoverContainer
        ref={rootRef}
        width={width}
        placement={effectivePlacement}
        style={contentStyle}
        attributes={contentAttributes}
        role={role}
        aria-label={ariaLabel}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={onKeyDown}
        innerRef={contentRef}
        wrapperClassName={c(
          s[`placement-${effectivePlacement}`],
          effectiveSecondaryPlacement &&
            s[`placement-${effectiveSecondaryPlacement}`],
          className
        )}
        contentClassName={c(s['content'], contentClassName)}
        arrow={
          <div
            {...arrowAttributes}
            ref={arrowRef}
            className={c(
              s['arrow'],
              s[`arrow-${effectivePlacement}`],
              s[`arrow-${effectiveSecondaryPlacement}`],
              arrowClassName
            )}
            style={arrowStyle}
          />
        }
        onClickOutside={onClickOutside}
        updatePositioning={updatePositioning}
      >
        {children}
      </PopoverContainer>
    </ThemeOverride>
  );
};
PopoverWrapper.displayName = 'PopoverWrapper';

export default PopoverWrapper;
